/**
 * Tab Leader Election
 *
 * Uses BroadcastChannel + localStorage to elect a single "leader" tab
 * that handles polling and SSE connections. Follower tabs listen for
 * broadcast updates instead of making their own requests.
 *
 * If the leader tab closes, another tab claims leadership.
 * Follows the same pattern as SidebarContext.js for cross-tab communication.
 */

const LEADER_KEY = 'hrp_tab_leader';
const HEARTBEAT_INTERVAL = 5000; // 5s heartbeat
const LEADER_TIMEOUT = 10000;    // 10s before assuming leader is dead

let tabId = null;
let isLeader = false;
let heartbeatTimer = null;
let claimTimer = null;
let listeners = new Set();

/**
 * Generate a unique ID for this tab
 */
function getTabId() {
    if (!tabId) {
        tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }
    return tabId;
}

/**
 * Try to claim leadership
 */
function claimLeadership() {
    const now = Date.now();
    const current = getLeaderInfo();

    // Claim if no leader or leader is stale
    if (!current || (now - current.heartbeat > LEADER_TIMEOUT)) {
        const info = { tabId: getTabId(), heartbeat: now };
        try {
            localStorage.setItem(LEADER_KEY, JSON.stringify(info));
        } catch (_e) { /* localStorage quota errors — ignore */ }

        // Verify we actually got it (race condition guard)
        const verify = getLeaderInfo();
        if (verify && verify.tabId === getTabId()) {
            if (!isLeader) {
                isLeader = true;
                startHeartbeat();
                notifyListeners();
            }
        }
    }
}

/**
 * Get current leader info from localStorage
 */
function getLeaderInfo() {
    try {
        const raw = localStorage.getItem(LEADER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
}

/**
 * Send heartbeat to keep leadership
 */
function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
        if (!isLeader) {
            stopHeartbeat();
            return;
        }
        const info = { tabId: getTabId(), heartbeat: Date.now() };
        try {
            localStorage.setItem(LEADER_KEY, JSON.stringify(info));
        } catch (_e) { /* ignore */ }
    }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

/**
 * Release leadership (on tab close/hide)
 */
function releaseLeadership() {
    if (!isLeader) return;
    isLeader = false;
    stopHeartbeat();
    try {
        const current = getLeaderInfo();
        if (current && current.tabId === getTabId()) {
            localStorage.removeItem(LEADER_KEY);
        }
    } catch (_e) { /* ignore */ }
    notifyListeners();
}

/**
 * Periodically check if leader is alive and try to claim if not
 */
function startClaimPolling() {
    stopClaimPolling();
    claimTimer = setInterval(() => {
        if (!isLeader) {
            claimLeadership();
        }
    }, LEADER_TIMEOUT);
}

function stopClaimPolling() {
    if (claimTimer) {
        clearInterval(claimTimer);
        claimTimer = null;
    }
}

/**
 * Notify all listeners of leadership change
 */
function notifyListeners() {
    for (const cb of listeners) {
        try { cb(isLeader); } catch (_e) { /* ignore */ }
    }
}

/**
 * Handle storage events from other tabs
 */
function handleStorageChange(e) {
    if (e.key !== LEADER_KEY) return;

    if (!e.newValue) {
        // Leader was removed — try to claim
        setTimeout(() => claimLeadership(), Math.random() * 500);
    } else {
        try {
            const info = JSON.parse(e.newValue);
            if (info.tabId !== getTabId() && isLeader) {
                // Another tab claimed leadership
                isLeader = false;
                stopHeartbeat();
                notifyListeners();
            }
        } catch (_e) { /* ignore */ }
    }
}

// Initialize on module load (guard for SSR/test environments)
function init() {
    if (typeof window === 'undefined') return;
    getTabId();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('beforeunload', releaseLeadership);
    claimLeadership();
    startClaimPolling();
}

init();

/**
 * Subscribe to leadership changes.
 * Callback receives (isLeader: boolean).
 * Returns unsubscribe function.
 */
export function onLeaderChange(callback) {
    listeners.add(callback);
    // Immediately notify current state
    callback(isLeader);
    return () => listeners.delete(callback);
}

/**
 * Check if this tab is the leader
 */
export function isTabLeader() {
    return isLeader;
}

/**
 * Force release (for testing or tab-hidden logic)
 */
export function forceRelease() {
    releaseLeadership();
}

/**
 * Force claim attempt (for tab-visible logic)
 */
export function forceClaim() {
    claimLeadership();
}
